import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOnboarding } from '../context/OnboardingContext';
import VisualizationRenderer from './visualizations/VisualizationRenderer';
import ProgressIndicator, { CompactProgressIndicator } from './ProgressIndicator';
import useSSE from '../hooks/useSSE';
import mainLogo from '../assets/images/mainlogo.png';
import '../styles/floating-ai-assistant.css';
import '../styles/ai-chatbot-mobile-fixes.css';
import '../styles/onboarding-modal.css';
import { useIsMobile } from '../hooks/useIsMobile';
import { extractVisualizations } from '../utils/extractVisualizations';

// Strip XML tool call tags from AI messages (e.g. <get_stock_price>, <symbol>, etc.)
const cleanAIMessage = (content) => {
  if (!content || typeof content !== 'string') return content;
  // Remove XML-style tool call blocks (e.g. <get_stock_price><symbol>AAPL</symbol></get_stock_price>)
  let cleaned = content.replace(/<\/?(?:get_stock_price|get_historical_prices|symbol|days|get_portfolio|analyze_stock|search_stocks|get_market_overview|get_asset_details|tool_call|function_call|parameters|parameter)[^>]*>/gi, '');
  // Remove any remaining XML-like tags that look like tool calls (snake_case tags)
  cleaned = cleaned.replace(/<\/?[a-z_]{3,}[^>]*>/g, '');
  // Clean up excessive whitespace left behind
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
  return cleaned;
};

const FloatingAIAssistant = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasCompletedOnboarding, startOnboardingFromChat } = useOnboarding();
  // Only finance mode now - removed mode state
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const isMobile = useIsMobile();
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Conversation management state
  const [sessionId, setSessionId] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [conversationList, setConversationList] = useState([]);
  const [showConversationPicker, setShowConversationPicker] = useState(false);

  // SSE Progress state
  const [currentProgress, setCurrentProgress] = useState(null);
  const [sseSessionId, setSseSessionId] = useState(null);
  const [useSSEMode, setUseSSEMode] = useState(true); // Enable SSE by default

  // Layout mode state for visualization support
  const [layoutMode, setLayoutMode] = useState('compact'); // Always 'compact' on mobile, other modes disabled
  // Onboarding-related state
  const [showAssessmentPrompt, setShowAssessmentPrompt] = useState(false);
  const [onboardingContext, setOnboardingContext] = useState(null);

  // Nudging system state
  const [nudgeCount, setNudgeCount] = useState(0);
  const [lastNudgeTime, setLastNudgeTime] = useState(0);
  const MAX_NUDGES_PER_SESSION = 3;
  const NUDGE_COOLDOWN = 30000; // 30 seconds between nudges
  
  // Request management for preventing duplicates
  const currentRequest = useRef(null);
  const requestCache = useRef(new Map());
  const dragTimeout = useRef(null);
  const dropLocked = useRef(false);
  const CACHE_DURATION = 5000; // 5 seconds
  const DEBOUNCE_DELAY = 300; // 300ms

  // LocalStorage keys
  const ACTIVE_SESSION_KEY = 'gideon_popup_active_session';

  // Generate a proper UUID for sessionId
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const messagesEndRef = useRef(null);
  const chatInterfaceRef = useRef(null);
  const lastAiMessageRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current && messagesEndRef.current.parentNode) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToTopOfResponse = () => {
    if (lastAiMessageRef.current) {
      lastAiMessageRef.current.scrollIntoView({ 
        behavior: "smooth", 
        block: "start" 
      });
    }
  };

  // Handle scroll detection for scroll-to-bottom button
  const handleScroll = (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;
    setShowScrollButton(!isNearBottom && scrollHeight > clientHeight);
  };

  // Conversation management functions
  const startNewConversation = useCallback(() => {
    const newSessionId = generateUUID();
    console.log(`[Popup AI] Starting new conversation: ${newSessionId}`);

    setSessionId(newSessionId);
    setMessages([{
      type: 'ai',
      content: "Hey! I'm Gideon, your AI financial guide. Ask me anything about stocks, investing, or markets.",
      timestamp: new Date()
    }]);
    setShowConversationPicker(false);

    localStorage.setItem(ACTIVE_SESSION_KEY, newSessionId);

    setConversationList(prev => [{
      sessionId: newSessionId,
      totalMessages: 0,
      lastActivity: new Date().toISOString(),
      questionsAsked: 0,
      stocksDiscussed: []
    }, ...prev]);
  }, [ACTIVE_SESSION_KEY]);

  const switchToConversation = useCallback((targetSessionId) => {
    if (targetSessionId === sessionId) {
      setShowConversationPicker(false);
      return;
    }

    console.log(`[Popup AI] Switching to: ${targetSessionId}`);
    setMessages([]);
    setSessionId(targetSessionId);
    setShowConversationPicker(false);
  }, [sessionId]);

  const deleteConversation = useCallback(async (targetSessionId) => {
    try {
      const token = localStorage.getItem('userToken');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(`${apiUrl}/api/agent/conversations/${targetSessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        setConversationList(prev =>
          prev.filter(conv => conv.sessionId !== targetSessionId)
        );

        if (targetSessionId === sessionId) {
          localStorage.removeItem(ACTIVE_SESSION_KEY);
          startNewConversation();
        }
      }
    } catch (error) {
      console.error('[Popup AI] Delete error:', error);
    }
  }, [sessionId, startNewConversation, ACTIVE_SESSION_KEY]);

  // Request cancellation and deduplication utilities
  const createRequestHash = (text) => {
    try {
      return btoa(text).slice(0, 16); // Simple hash for deduplication
    } catch (e) {
      return text.substring(0, 16); // Fallback for non-ASCII characters
    }
  };

  const cancelCurrentRequest = () => {
    if (currentRequest.current) {
      console.log('🚫 Cancelling previous request');
      currentRequest.current.abort();
      currentRequest.current = null;
    }
  };

  const clearDragTimeout = () => {
    if (dragTimeout.current) {
      console.log('🧹 Clearing drag timeout');
      clearTimeout(dragTimeout.current);
      dragTimeout.current = null;
    }
  };

  const getCachedResult = (questionHash) => {
    const cached = requestCache.current.get(questionHash);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log('📋 Using cached result for duplicate request');
      return cached.result;
    }
    return null;
  };

  const setCachedResult = (questionHash, result) => {
    requestCache.current.set(questionHash, {
      result,
      timestamp: Date.now()
    });

    // Clean old cache entries
    const now = Date.now();
    for (const [key, value] of requestCache.current.entries()) {
      if (now - value.timestamp > CACHE_DURATION) {
        requestCache.current.delete(key);
      }
    }
  };

  // Trading intent detection for nudging
  const detectTradingIntent = (messageText) => {
    if (!messageText) return false;
    
    const tradingKeywords = [
      'buy', 'sell', 'invest', 'stock', 'portfolio', 'trade', 'trading',
      'should i buy', 'worth buying', 'good investment', 'recommend',
      'price target', 'when to sell', 'hold or sell', 'market outlook'
    ];
    
    const lowerMessage = messageText.toLowerCase();
    return tradingKeywords.some(keyword => lowerMessage.includes(keyword));
  };

  // Check if we should show a nudge
  const shouldShowNudge = (messageText) => {
    // Don't nudge if user has completed onboarding
    if (hasCompletedOnboarding) return false;
    
    // Don't nudge if we've reached max nudges
    if (nudgeCount >= MAX_NUDGES_PER_SESSION) return false;
    
    // Don't nudge if too soon since last nudge
    const now = Date.now();
    if (now - lastNudgeTime < NUDGE_COOLDOWN) return false;
    
    // Check for trading intent
    const hasTradingIntent = detectTradingIntent(messageText);
    
    // Show nudge if trading intent OR 20% random chance for any question
    return hasTradingIntent || Math.random() < 0.2;
  };

  // Add a nudge message
  const addNudgeMessage = () => {
    const nudgeMessages = [
      "💡 I could give you even more personalized advice if we had that quick chat! Want me to learn your style? 🎯",
      "🎯 Ready to get advice tailored just for you? A 2-minute chat would help me understand your investment style better!",
      "✨ I notice you're asking great questions! Want me to personalize my responses to match your learning style?"
    ];
    
    const randomMessage = nudgeMessages[Math.floor(Math.random() * nudgeMessages.length)];
    
    const nudgeMessageObj = {
      type: 'nudge',
      content: randomMessage,
      timestamp: new Date(),
      id: Date.now()
    };
    
    setMessages(prev => [...prev, nudgeMessageObj]);
    setNudgeCount(prev => prev + 1);
    setLastNudgeTime(Date.now());
    
    // Show assessment prompt
    setShowAssessmentPrompt({
      show: true,
      message: "Let me get to know you better!",
      benefits: [
        "Get advice matched to YOUR goals",
        "Learn at YOUR preferred pace",
        "Understand YOUR risk comfort zone"
      ]
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      // Get the last message
      const lastMessage = messages[messages.length - 1];
      
      // If the last message is from AI, scroll to top of response
      // Otherwise scroll to bottom for user messages
      if (lastMessage?.type === 'ai') {
        scrollToTopOfResponse();
      } else {
        scrollToBottom();
      }
    }, 100); // Slight delay to ensure DOM is updated
    return () => clearTimeout(timer);
  }, [messages.length]);


  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      console.log('🧹 FloatingAIAssistant: Cleaning up on unmount');
      cancelCurrentRequest();
      clearDragTimeout();
      requestCache.current.clear();
    };
  }, []);

  const generateTickerPrompt = (data) => {
    const { ticker, name, price, type } = data;
    const assetType = type === 'stock' ? 'stock' : 
                     type === 'etf' ? 'ETF' : 
                     type === 'crypto-etf' ? 'Crypto ETF' : 
                     type === 'bond-etf' ? 'Bond ETF' : 'asset';
    
    return `Analyze ${ticker} (${name}) for me. Current price is $${price?.toFixed(2) || 'N/A'}. 
    
Please provide insights on:
1. Market trends and recent performance
2. How this ${assetType} fits into my investment portfolio
3. Risk assessment and growth potential
4. Any recommendations based on my investment profile

Consider my current portfolio position and market conditions.`;
  };

  const generateFinancialPrompt = (data) => {
    const { metric, value, displayValue, breakdown, userName } = data;
    
    if (metric === 'net_worth') {
      const { cash, savingsBalance, totalInvested, loan } = breakdown;
      
      // Calculate percentages for better analysis
      const totalAssets = cash + savingsBalance + totalInvested;
      const debtRatio = totalAssets > 0 ? ((loan / totalAssets) * 100).toFixed(0) : 0;

      // Create a concise prompt under 500 characters
      return `Analyze my finances: Net worth ${displayValue}. Cash $${Math.round(cash)}, Savings $${Math.round(savingsBalance)}, Investments $${Math.round(totalInvested)}, Loan -$${Math.round(loan)}. Debt ratio ${debtRatio}%. What are my top 3 action items to improve my net worth? Keep it practical and educational.`;
    }
    
    return `Explain my financial data: ${JSON.stringify(data)}`.substring(0, 499);
  };

  // Detect suitability hints in AI responses
  const detectSuitabilityHint = (responseData, originalQuestion = '') => {
    // Check if response contains suitability hint for users who haven't completed onboarding
    if (responseData.suitabilityHint && responseData.suitabilityHint.required && !hasCompletedOnboarding) {
      return {
        required: true,
        message: responseData.suitabilityHint.gideonPrompt || "Let's have a quick chat so I can give you personalized investment advice!",
        benefits: responseData.suitabilityHint.benefits || [
          'Get personalized investment recommendations',
          'Learn at your own pace',
          'Get personalized trading advice'
        ],
        action: responseData.suitabilityHint.action || 'START_CHAT_ASSESSMENT',
        originalQuestion: originalQuestion // Store the original question for context
      };
    }
    return null;
  };

  // Handle starting the assessment from the prompt
  const handleStartAssessment = async (originalQuestion) => {
    console.log('🚀 Starting assessment from AI assistant with context:', originalQuestion);
    
    // Extract stock ticker if mentioned
    const stockRegex = /\b([A-Z]{1,5})\b/g;
    const matches = originalQuestion.match(stockRegex);
    const stockMentioned = matches ? matches[0] : null;
    
    const context = {
      stockMentioned,
      originalQuestion,
      source: 'ai_assistant'
    };
    
    try {
      const result = await startOnboardingFromChat(context);
      if (result.success) {
        navigate('/onboarding', {
          state: {
            context: context,
            source: 'ai_popup'
          }
        });
        setShowAssessmentPrompt(false);
      }
    } catch (error) {
      console.error('Failed to start onboarding:', error);
      const errorMessage = {
        type: 'ai',
        content: "Sorry, I'm having trouble right now. Please try again in a moment!",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  // Initialize conversations on mount
  useEffect(() => {
    const initializeChat = async () => {
      setIsLoadingSession(true);

      try {
        const token = localStorage.getItem('userToken');

        if (!token) {
          console.log('[Popup AI] No auth token');
          startNewConversation();
          setIsLoadingSession(false);
          return;
        }

        const savedSessionId = localStorage.getItem(ACTIVE_SESSION_KEY);
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        const response = await fetch(`${apiUrl}/api/agent/conversations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const data = await response.json();

        if (data.success && data.data && data.data.length > 0) {
          console.log(`[Popup AI] Found ${data.data.length} conversations`);
          setConversationList(data.data);

          if (savedSessionId) {
            const exists = data.data.some(c => c.sessionId === savedSessionId);
            if (exists) {
              setSessionId(savedSessionId);
            } else {
              localStorage.removeItem(ACTIVE_SESSION_KEY);
              // Use most recent conversation
              setSessionId(data.data[0].sessionId);
              localStorage.setItem(ACTIVE_SESSION_KEY, data.data[0].sessionId);
            }
          } else {
            // Use most recent conversation
            setSessionId(data.data[0].sessionId);
            localStorage.setItem(ACTIVE_SESSION_KEY, data.data[0].sessionId);
          }
        } else {
          startNewConversation();
        }
      } catch (error) {
        console.error('[Popup AI] Error loading conversations:', error);
        startNewConversation();
      } finally {
        setIsLoadingSession(false);
      }
    };

    initializeChat();
  }, [startNewConversation, ACTIVE_SESSION_KEY]);

  // Load messages when sessionId changes
  useEffect(() => {
    if (!sessionId || isLoadingSession) return;

    const loadMessages = async () => {
      try {
        const token = localStorage.getItem('userToken');
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        const response = await fetch(`${apiUrl}/api/agent/conversations/${sessionId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        const data = await response.json();

        if (data.success && data.data && data.data.messages) {
          console.log(`[Popup AI] Loaded ${data.data.messages.length} messages`);

          // Transform messages from backend format to frontend format
          const transformedMessages = data.data.messages.map(msg => ({
            type: msg.role === 'user' ? 'user' : 'ai',
            content: msg.content,
            timestamp: new Date(msg.timestamp),
            suggestions: msg.suggestions || [],
            hasVisualContent: msg.hasVisualContent,
            visualizations: msg.visualizations
          }));

          setMessages(transformedMessages);
          localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
        } else {
          console.warn(`[Popup AI] Conversation ${sessionId} not found or empty`);
          // Start with welcome message for new conversation
          setMessages([{
            type: 'ai',
            content: "Hey! I'm Gideon, your AI financial guide. Ask me anything about stocks, investing, or markets.",
            timestamp: new Date()
          }]);
          localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
        }
      } catch (error) {
        console.error('[Popup AI] Error loading conversation:', error);
        // Start with welcome message on error
        setMessages([{
          type: 'ai',
          content: "Hey! I'm Gideon, your AI financial guide. Ask me anything about stocks, investing, or markets.",
          timestamp: new Date()
        }]);
      }
    };

    loadMessages();
  }, [sessionId, isLoadingSession, ACTIVE_SESSION_KEY]);

  // Handle touch gestures for mobile - only on header area
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientY);
    setTouchEnd(null); // Reset touch end
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isSwipeUp = distance > 80;    // Increased threshold for more intentional swipes
    const isSwipeDown = distance < -80;  // Increased threshold for more intentional swipes

    // Only allow swipe down to minimize (swipe up to expand handled by icon click)
    if (isSwipeDown && !isMinimized) {
      console.log('🎯 Intentional swipe down detected - minimizing');
      setIsMinimized(true);
    }
    
    // Reset touch values
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Debug: Track SSE progress state changes
  useEffect(() => {
    console.log('[FloatingAI] 🔍 State update - isLoading:', isLoading, 'currentProgress:', currentProgress, 'sseSessionId:', sseSessionId);
  }, [isLoading, currentProgress, sseSessionId]);

  // SSE Callbacks
  const handleSSEProgress = useCallback((event) => {
    console.log('[FloatingAI] 📊 SSE PROGRESS RECEIVED:', event.step, event.percentage + '%');
    console.log('[FloatingAI] 📊 Current isMinimized:', isMinimized);
    setCurrentProgress(event);
  }, [isMinimized]);

  const handleSSEComplete = useCallback((data) => {
    console.log('[FloatingAI] ✅ handleSSEComplete called with:', data);

    // Add AI response to messages
    const aiMessage = {
      type: 'ai',
      content: data.message,
      suggestions: data.suggestions || [],
      hasVisualContent: data.hasVisualContent,
      visualizations: extractVisualizations(data),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, aiMessage]);
    setIsLoading(false);
    setCurrentProgress(null);
    setSseSessionId(null);

    // Update conversation metadata
    setConversationList(prev =>
      prev.map(conv =>
        conv.sessionId === sessionId
          ? {
              ...conv,
              totalMessages: conv.totalMessages + 2,
              lastActivity: new Date().toISOString()
            }
          : conv
      )
    );

    // Check for suitability hints
    const suitabilityHint = detectSuitabilityHint(data, ''); // Empty string as we don't have the original question here
    if (suitabilityHint) {
      setTimeout(() => {
        setShowAssessmentPrompt(suitabilityHint);
      }, 1000);
    }
  }, [sessionId]);

  const handleSSEError = useCallback((error) => {
    console.error('[SSE Error]', error);
    setIsLoading(false);
    setCurrentProgress(null);
    setSseSessionId(null);
    
    const errorMessage = {
      type: 'ai',
      content: error.message || 'An error occurred while processing your request.',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, errorMessage]);
  }, []);

  // Setup SSE hook
  const { isConnected } = useSSE(
    sseSessionId,
    handleSSEProgress,
    handleSSEComplete,
    handleSSEError,
    {
      apiUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000',
      autoReconnect: true,
      maxReconnectAttempts: 3
    }
  );

  const handleSendMessage = useCallback(async () => {
    if (!inputValue.trim() || isLoading) return;

    // Cancel any pending request
    cancelCurrentRequest();

    // Check for cached result
    const questionHash = createRequestHash(inputValue);
    const cachedResult = getCachedResult(questionHash);
    if (cachedResult) {
      console.log('📋 Using cached result for manual message');
      const aiMessage = {
        type: 'ai',
        content: cachedResult.message,
        suggestions: cachedResult.suggestions || [],
        hasVisualContent: cachedResult.hasVisualContent,
        visualizations: extractVisualizations(cachedResult),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      setInputValue('');
      return;
    }

    // Check JWT token
    const token = localStorage.getItem('userToken');
    if (!token) {
      console.error('❌ No JWT token found - user needs to login');
      const errorMessage = {
        type: 'ai',
        content: "Please log in to use the AI assistant.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    const currentInput = inputValue; // Store current input
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    // Check if SSE mode is enabled
    if (useSSEMode) {
      try {
        // Use async endpoint with SSE progress
        const endpoint = '/api/agent/ask/async';
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        
        console.log('🚀 Starting SSE request');
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            question: currentInput,
            sessionId: sessionId
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('📊 SSE Session started:', data);
        
        if (data.success && data.sessionId) {
          // Set session ID to trigger SSE connection
          setSseSessionId(data.sessionId);
          
          // Show initial progress
          setCurrentProgress({
            type: 'progress',
            step: 'Connecting...',
            message: 'Establishing connection to AI assistant',
            percentage: 0
          });
        } else {
          throw new Error(data.message || 'Failed to start processing');
        }
      } catch (error) {
        console.error('❌ Error starting SSE session:', error);
        // Fall back to regular mode
        setUseSSEMode(false);
        // Retry with regular endpoint
        handleSendMessageRegular(currentInput, token);
      }
      return;
    }

    // Regular non-SSE mode
    handleSendMessageRegular(currentInput, token);
  }, [inputValue, isLoading, sessionId, useSSEMode]);

  // Regular send message (fallback)
  const handleSendMessageRegular = useCallback(async (messageText, token) => {
    // Create new AbortController
    currentRequest.current = new AbortController();
    
    // Create question hash for this message
    const questionHash = createRequestHash(messageText);

    try {
      const endpoint = '/api/agent/ask';
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      console.log('🤖 Sending AI request with cancellation support');
      console.log('🤖 Request payload:', { question: messageText, sessionId: sessionId });
      console.log('🤖 Request URL:', `${apiUrl}${endpoint}`);
      console.log('🤖 Question length:', messageText.length);
      
      // Demo mode bypass for AI assistant
      if (process.env.REACT_APP_DEMO_MODE === 'true') {
        console.log('🎭 DEMO MODE: Simulating AI response for visualization testing');
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Generate demo response with chart visualization
        const demoResponse = {
          success: true,
          data: {
            response: `Here's a demo chart visualization for your request: "${messageText}". In demo mode, I can show you how the dark mode and mobile responsive features work!`,
            visualizations: [{
              type: 'line_chart',
              title: 'Demo Stock Chart - Dark Mode Ready',
              subtitle: 'Sample data showing mobile-optimized visualization',
              data: {
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                datasets: [{
                  label: 'Demo Stock Price',
                  data: [65, 75, 70, 81, 76, 85],
                  borderColor: '#3b82f6',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  tension: 0.4
                }]
              },
              metadata: {
                timeframe: 'Last 6 months',
                source: 'Demo Data',
                lastUpdated: new Date().toISOString()
              }
            }]
          }
        };
        
        const aiMessage = {
          type: 'ai',
          content: demoResponse.data.response,
          visualizations: demoResponse.data.visualizations,
          timestamp: new Date(),
          isDemoResponse: true
        };
        
        setMessages(prev => [...prev, aiMessage]);
        setIsLoading(false);
        return;
      }
      
      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question: messageText,
          sessionId: sessionId
        }),
        signal: currentRequest.current.signal // Enable request cancellation
      });

      console.log('🤖 API Response status:', response.status);
      console.log('🤖 API Response headers:', response.headers);
      
      // Get response text for better error debugging
      const responseText = await response.text();
      console.log('🤖 API Response text:', responseText);

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        console.error('❌ Authentication failed - invalid or expired token');
        localStorage.removeItem('userToken');
        const authErrorMessage = {
          type: 'ai',
          content: "Your session has expired. Please log in again to use the AI assistant.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, authErrorMessage]);
        return;
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = JSON.parse(responseText);
        } catch (e) {
          errorData = { message: responseText || response.statusText };
        }
        console.log('🤖 Error data parsed:', errorData);
        console.log('🤖 Validation errors:', errorData.errors);
        
        // If 400 error with validation errors, show them clearly
        if (response.status === 400 && errorData.errors && Array.isArray(errorData.errors)) {
          const validationDetails = errorData.errors.map(err => 
            typeof err === 'object' ? JSON.stringify(err) : err
          ).join(', ');
          throw new Error(`HTTP 400: ${errorData.message} - Errors: ${validationDetails}`);
        }
        
        throw new Error(`HTTP ${response.status}: ${errorData.message || response.statusText}`);
      }

      const data = JSON.parse(responseText);
      console.log('📊 API Response Data (sendMessage):', data);
      console.log('📊 Visualizations in response:', data.visualizations);

      if (data.success) {
        // Cache the result
        setCachedResult(questionHash, data);

        const visualizations = extractVisualizations(data);
        const hasVisualContent = data.hasVisualContent || visualizations.length > 0;

        const aiMessage = {
          type: 'ai',
          content: data.message,
          suggestions: data.suggestions || [],
          hasVisualContent: hasVisualContent,
          visualizations: visualizations,
          timestamp: new Date()
        };
        console.log('📊 CHART DEBUG (sendMessage) - Final AI Message:', aiMessage);
        console.log('📊 CHART DEBUG (sendMessage) - Message has visualizations:', aiMessage.visualizations?.length > 0);
        setMessages(prev => [...prev, aiMessage]);

        // Update conversation metadata
        setConversationList(prev =>
          prev.map(conv =>
            conv.sessionId === sessionId
              ? {
                  ...conv,
                  totalMessages: conv.totalMessages + 2,
                  lastActivity: new Date().toISOString()
                }
              : conv
          )
        );

        // Check for suitability hints and show assessment prompt if needed
        const suitabilityHint = detectSuitabilityHint(data, inputValue);
        if (suitabilityHint) {
          console.log('🎯 Suitability hint detected in handleSendMessage:', suitabilityHint);
          setTimeout(() => {
            setShowAssessmentPrompt(suitabilityHint);
          }, 1000); // Small delay so user can read the AI response first
        }
      } else {
        throw new Error(data.message || 'Failed to get response');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🚫 Manual request was cancelled');
        return; // Don't show error for cancelled requests
      }
      
      console.error('❌ Error sending message:', error);
      console.error('❌ Error type:', error.constructor.name);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error details:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Full error object:', error);
      console.error('❌ Error occurred at fetch or parsing stage');
      const errorMessage = {
        type: 'ai',
        content: `Sorry, I'm having trouble right now. Error: ${error.message || 'Unknown error'} (Status: ${error.response?.status || 'N/A'}). Can you try asking again?`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      currentRequest.current = null;
    }
  }, [inputValue, isLoading, sessionId]);

  // Regular sendMessageDirectly handler (fallback - defined first for dependency order)
  const handleSendMessageDirectlyRegular = useCallback(async (messageText, token, questionHash) => {
    // Create new AbortController
    currentRequest.current = new AbortController();

    try {
      const endpoint = '/api/agent/ask';
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      console.log('🤖 Sending direct AI request with cancellation support');

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          question: messageText,
          sessionId: sessionId
        }),
        signal: currentRequest.current.signal
      });

      console.log('🤖 Direct API Response status:', response.status);

      if (response.status === 401 || response.status === 403) {
        console.error('❌ Authentication failed - invalid or expired token');
        localStorage.removeItem('userToken');
        const authErrorMessage = {
          type: 'ai',
          content: "Your session has expired. Please log in again to use the AI assistant.",
          timestamp: new Date()
        };
        setMessages(prev => [...prev, authErrorMessage]);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 API Response Data:', data);

      if (data.success) {
        setCachedResult(questionHash, data);

        const visualizations = extractVisualizations(data);
        const hasVisualContent = data.hasVisualContent || visualizations.length > 0;

        const aiMessage = {
          type: 'ai',
          content: data.message,
          suggestions: data.suggestions || [],
          hasVisualContent: hasVisualContent,
          visualizations: visualizations,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);

        const suitabilityHint = detectSuitabilityHint(data, messageText);
        if (suitabilityHint) {
          setTimeout(() => {
            setShowAssessmentPrompt(suitabilityHint);
          }, 1000);
        }

        if (shouldShowNudge(messageText)) {
          setTimeout(() => {
            addNudgeMessage();
          }, 2000);
        }
      } else {
        throw new Error(data.message || 'Failed to get response');
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🚫 Request was cancelled');
        return;
      }

      console.error('❌ Error sending direct message:', error);
      const errorMessage = {
        type: 'ai',
        content: `Sorry, I'm having trouble right now. Error: ${error.message || 'Unknown error'}. Can you try asking again?`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      currentRequest.current = null;
    }
  }, [sessionId]);

  // Enhanced sendMessageDirectly with request cancellation and deduplication
  const sendMessageDirectly = useCallback(async (messageText) => {
    console.log('🚀 sendMessageDirectly called with text length:', messageText?.length);
    console.log('🚀 isLoading:', isLoading, 'messageText exists:', !!messageText?.trim());

    if (!messageText?.trim()) {
      console.log('❌ sendMessageDirectly: No message text provided');
      return;
    }

    // Check if already processing
    if (isLoading) {
      console.log('⏳ Already processing request, ignoring duplicate');
      return;
    }

    // Check cache for duplicate requests
    const questionHash = createRequestHash(messageText);
    const cachedResult = getCachedResult(questionHash);
    if (cachedResult) {
      const aiMessage = {
        type: 'ai',
        content: cachedResult.message,
        suggestions: cachedResult.suggestions || [],
        hasVisualContent: cachedResult.hasVisualContent,
        visualizations: extractVisualizations(cachedResult),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      return;
    }

    // Cancel any pending request
    cancelCurrentRequest();

    // Check JWT token
    const token = localStorage.getItem('userToken');
    console.log('🔐 sendMessageDirectly - Token check:', token ? `Token exists (${token.substring(0, 20)}...)` : 'No token');
    if (!token) {
      console.error('❌ No JWT token found - user needs to login');
      const errorMessage = {
        type: 'ai',
        content: "Please log in to use the AI assistant.",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      return;
    }

    const userMessage = {
      type: 'user',
      content: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue(''); // Clear input after sending
    setIsLoading(true);

    // Use SSE mode if enabled (same as handleSendMessage)
    if (useSSEMode) {
      try {
        const endpoint = '/api/agent/ask/async';
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

        console.log('🚀 sendMessageDirectly: Starting SSE request');
        const response = await fetch(`${apiUrl}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            question: messageText,
            sessionId: sessionId
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('📊 SSE Session started (sendMessageDirectly):', data);

        if (data.success && data.sessionId) {
          console.log('[FloatingAI] 🚀 SSE session started, setting sessionId:', data.sessionId);
          // Set session ID to trigger SSE connection
          setSseSessionId(data.sessionId);

          // Show initial progress
          console.log('[FloatingAI] 🎬 Setting initial currentProgress');
          setCurrentProgress({
            type: 'progress',
            step: 'Analyzing...',
            message: 'Processing your request',
            percentage: 0
          });
        } else {
          throw new Error(data.message || 'Failed to start processing');
        }
      } catch (error) {
        console.error('[FloatingAI] ❌ Error starting SSE session (sendMessageDirectly):', error);
        // Fall back to regular mode
        setIsLoading(false);
        setCurrentProgress(null);
        // Retry with regular endpoint by calling the regular handler
        handleSendMessageDirectlyRegular(messageText, token, questionHash);
      }
      return;
    }

    // Regular non-SSE mode
    handleSendMessageDirectlyRegular(messageText, token, questionHash);
  }, [isLoading, sessionId, useSSEMode, handleSendMessageDirectlyRegular]);

  // Enhanced ticker drop handler with debouncing and deduplication
  const handleTickerDropWithDebounce = useCallback((data) => {
    console.log('🎯 AI ASSISTANT: Processing ticker drop with debouncing:', data);
    
    // Clear any existing timeout
    clearDragTimeout();
    
    // Check if we're already processing
    if (isLoading) {
      console.log('⏳ Already processing, ignoring duplicate drop');
      return;
    }

    // Generate contextual prompt
    const prompt = generateTickerPrompt(data);
    console.log('📝 AI ASSISTANT: Generated prompt length:', prompt.length);
    
    // Check cache to avoid duplicate analysis
    const questionHash = createRequestHash(prompt);
    const cachedResult = getCachedResult(questionHash);
    if (cachedResult) {
      console.log('📋 Using cached result for ticker analysis');
      const aiMessage = {
        type: 'ai',
        content: cachedResult.message,
        suggestions: cachedResult.suggestions || [],
        hasVisualContent: cachedResult.hasVisualContent,
        visualizations: extractVisualizations(cachedResult),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Auto-expand if minimized
      if (isMinimized) {
        setIsMinimized(false);
      }
      return;
    }

    setDraggedItem(data);
    setInputValue(prompt);
    
    // Auto-expand if minimized
    if (isMinimized) {
      console.log('🔍 AI ASSISTANT: Expanding (was minimized)');
      setIsMinimized(false);
    }
    
    // Debounced API call
    dragTimeout.current = setTimeout(() => {
      console.log('🚀 AI ASSISTANT: Executing debounced sendMessageDirectly()');
      sendMessageDirectly(prompt);
    }, DEBOUNCE_DELAY);
  }, [isMinimized, isLoading, sendMessageDirectly]);

  // Listen for mobile ticker drops
  useEffect(() => {
    const handleTickerDrop = (event) => {
      const data = event.detail;
      handleTickerDropWithDebounce(data);
    };

    window.addEventListener('tickerDropped', handleTickerDrop);
    return () => {
      window.removeEventListener('tickerDropped', handleTickerDrop);
      clearDragTimeout(); // Clean up on unmount
    };
  }, [handleTickerDropWithDebounce]);

  // Enhanced financial drop handler with debouncing and deduplication
  const handleFinancialDropWithDebounce = useCallback((data) => {
    console.log('💰 AI ASSISTANT: Processing financial drop with debouncing:', data);
    
    // Clear any existing timeout
    clearDragTimeout();
    
    // Check if we're already processing
    if (isLoading) {
      console.log('⏳ Already processing, ignoring duplicate financial drop');
      return;
    }

    // Generate financial explanation prompt
    const prompt = generateFinancialPrompt(data);
    console.log('📝 AI ASSISTANT: Generated financial prompt length:', prompt.length);
    
    // Check cache to avoid duplicate analysis
    const questionHash = createRequestHash(prompt);
    const cachedResult = getCachedResult(questionHash);
    if (cachedResult) {
      console.log('📋 Using cached result for financial analysis');
      const aiMessage = {
        type: 'ai',
        content: cachedResult.message,
        suggestions: cachedResult.suggestions || [],
        hasVisualContent: cachedResult.hasVisualContent,
        visualizations: extractVisualizations(cachedResult),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiMessage]);
      
      // Auto-expand if minimized
      if (isMinimized) {
        setIsMinimized(false);
      }
      return;
    }

    setDraggedItem(data);
    setInputValue(prompt);
    
    // Auto-expand if minimized
    if (isMinimized) {
      console.log('🔍 AI ASSISTANT: Expanding for financial explanation (was minimized)');
      setIsMinimized(false);
    }
    
    // Debounced API call
    dragTimeout.current = setTimeout(() => {
      console.log('🚀 AI ASSISTANT: Executing debounced sendMessageDirectly() for financial data');
      sendMessageDirectly(prompt);
    }, DEBOUNCE_DELAY);
  }, [isMinimized, isLoading, sendMessageDirectly]);

  // Listen for financial data drops
  useEffect(() => {
    const handleFinancialDrop = (event) => {
      const data = event.detail;
      handleFinancialDropWithDebounce(data);
    };

    window.addEventListener('financialDataDropped', handleFinancialDrop);
    return () => {
      window.removeEventListener('financialDataDropped', handleFinancialDrop);
      clearDragTimeout(); // Clean up on unmount
    };
  }, [handleFinancialDropWithDebounce]);

  // Auto-switch to chart mode when visualizations are detected (disabled on mobile)
  useEffect(() => {
    const hasCharts = messages.some(msg => msg.hasVisualContent && msg.visualizations?.length > 0);
    
    // Don't auto-switch on mobile - charts work better in regular compact mode
    if (!isMobile && hasCharts && layoutMode === 'compact' && !isMinimized) {
      console.log('📊 Charts detected - switching to chart mode');
      setLayoutMode('chart');
    } else if (!hasCharts && layoutMode === 'chart') {
      console.log('📊 No charts detected - switching back to compact mode');
      setLayoutMode('compact');
    }
  }, [messages, layoutMode, isMinimized, isMobile]);

  // Reset to compact mode when switching to mobile
  useEffect(() => {
    if (isMobile && (layoutMode === 'chart' || layoutMode === 'fullscreen')) {
      console.log('📱 Switching to mobile - resetting to compact mode');
      setLayoutMode('compact');
    }
  }, [isMobile, layoutMode]);

  // Layout mode controls
  const toggleLayoutMode = () => {
    const modes = ['compact', 'chart', 'fullscreen'];
    const currentIndex = modes.indexOf(layoutMode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    setLayoutMode(nextMode);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
  };

  // Enhanced drag and drop handlers with drop locking
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    setIsDragOver(false);
    
    // Prevent multiple simultaneous drops
    if (dropLocked.current) {
      console.log('🔒 Drop locked, ignoring duplicate drop event');
      return;
    }

    dropLocked.current = true;
    setTimeout(() => {
      dropLocked.current = false;
    }, 500); // 500ms lock period

    // Check if already processing
    if (isLoading) {
      console.log('⏳ Already processing, ignoring desktop drop');
      return;
    }

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      console.log('🎯 DESKTOP DROP: Processing drop data:', data);
      
      // Generate contextual prompt based on data type
      let prompt;
      if (data.type === 'financial_data') {
        console.log('💰 DESKTOP DROP: Financial data detected');
        prompt = generateFinancialPrompt(data);
      } else {
        console.log('📈 DESKTOP DROP: Ticker data detected');
        prompt = generateTickerPrompt(data);
      }
      
      // Check cache to avoid duplicate analysis
      const questionHash = createRequestHash(prompt);
      const cachedResult = getCachedResult(questionHash);
      if (cachedResult) {
        console.log('📋 Using cached result for desktop drop');
        const aiMessage = {
          type: 'ai',
          content: cachedResult.message,
          suggestions: cachedResult.suggestions || [],
          hasVisualContent: cachedResult.hasVisualContent,
          visualizations: extractVisualizations(cachedResult),
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiMessage]);
        
        // Auto-expand if minimized
        if (isMinimized) {
          setIsMinimized(false);
        }
        return;
      }

      setDraggedItem(data);
      setInputValue(prompt);
      
      // Auto-expand if minimized
      if (isMinimized) {
        setIsMinimized(false);
      }
      
      // Clear any existing timeout and send with debouncing
      clearDragTimeout();
      dragTimeout.current = setTimeout(() => {
        console.log('🚀 DESKTOP DROP: Executing debounced sendMessageDirectly()');
        sendMessageDirectly(prompt);
      }, DEBOUNCE_DELAY);
      
    } catch (error) {
      console.error('❌ Error handling desktop drop:', error);
    }
  };

  // Removed toggleMode function - only finance mode now

  const toggleMinimized = () => {
    setIsMinimized(!isMinimized);
  };

  // Mobile quick questions
  const mobileQuickQuestions = [
    "What's an ETF?",
    "How do I buy stocks?",
    "Explain P/E ratio",
    "What's market cap?"
  ];

  if (!user) return null;

  // Debug: Log every render
  console.log('[FloatingAI] 🎨 RENDER - isLoading:', isLoading, 'hasProgress:', !!currentProgress, 'isMinimized:', isMinimized);

  return (
    <>
    <div 
      className={`floating-ai-assistant ${isMinimized ? 'minimized' : 'expanded'} ${!isMobile && layoutMode === 'chart' ? 'chart-mode' : ''} ${!isMobile && layoutMode === 'fullscreen' ? 'fullscreen' : ''} ${isDragOver ? 'drag-over' : ''}`}
      data-ai-assistant="true"
    >
      {/* Floating Icon */}
      <div className="floating-icons">
        <div 
          className={`icon-container active ${isDragOver ? 'drop-target' : ''}`}
          onClick={toggleMinimized}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          title="Gideon"
        >
          <div className="icon main-logo-icon">
            <img 
              src={mainLogo} 
              alt="Gideon" 
              width="40" 
              height="40"
            />
          </div>
          <span className="icon-label">Ask Me</span>
        </div>
      </div>

      {/* Chat Interface */}
      {!isMinimized && (
        <div 
          className="chat-interface"
          ref={chatInterfaceRef}
        >
          <div 
            className="chat-header"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="header-left">
              <div className="mode-indicator finance">
                <img 
                  src={mainLogo} 
                  alt="Gideon" 
                  width="32" 
                  height="32"
                  style={{ objectFit: 'contain' }}
                />
              </div>
              <div className="header-text">
                <h3>Gideon</h3>
                <div className="mode-status">
                  {hasCompletedOnboarding ? (
                    <p className="mode-personal-coach">
                      <span className="mode-icon">🎯</span>
                      Personal Coach Mode
                    </p>
                  ) : (
                    <p className="mode-basic-guide">
                      <span className="mode-icon">📖</span>
                      Basic Guide Mode
                    </p>
                  )}
                </div>
                <p className="subtitle">Ask about stocks, investments & market data</p>
              </div>
            </div>
            <div className="header-actions">
              {/* Conversation Controls */}
              <button
                onClick={() => setShowConversationPicker(!showConversationPicker)}
                title="View conversations"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '6px',
                  width: '36px',
                  height: '36px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  color: 'white',
                  position: 'relative'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
              >
                💬
                {conversationList.length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: '#059669',
                    color: 'white',
                    borderRadius: '10px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: 'bold'
                  }}>
                    {conversationList.length}
                  </span>
                )}
              </button>

              <button
                onClick={startNewConversation}
                title="New conversation"
                style={{
                  background: 'linear-gradient(135deg, #00e1c4, #5fffe6)',
                  border: 'none',
                  borderRadius: '6px',
                  width: '36px',
                  height: '36px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  color: '#0a0d14',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 8px rgba(0, 225, 196, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 225, 196, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 225, 196, 0.3)';
                }}
              >
                ➕
              </button>

              {/* Hide layout toggle on mobile - visualization mode doesn't work well in small containers */}
              {!isMobile && (
                <button
                  className="layout-toggle-btn"
                  onClick={toggleLayoutMode}
                  title={`Current: ${layoutMode} mode - Click to cycle`}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    width: '36px',
                    height: '36px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    marginRight: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                >
                  {layoutMode === 'compact' && '📄'}
                  {layoutMode === 'chart' && '📊'}
                  {layoutMode === 'fullscreen' && '🔍'}
                </button>
              )}

              {/* SSE Mode Toggle */}
              <button
                className={`sse-toggle-btn ${useSSEMode ? 'active' : ''}`}
                onClick={() => setUseSSEMode(!useSSEMode)}
                title={useSSEMode ? 'SSE Mode: ON (Real-time progress)' : 'SSE Mode: OFF (Standard mode)'}
                style={{
                  background: useSSEMode
                    ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                    : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  width: '36px',
                  height: '36px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  marginRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                ⚡
              </button>

              <button
                className="minimize-btn"
                onClick={toggleMinimized}
                title="Minimize"
              >
                −
              </button>
            </div>
          </div>

          <div className="chat-messages" onScroll={handleScroll}>
            {/* Debug: Show progress state */}
            {console.log('[FloatingAI] 🎨 Render check - isLoading:', isLoading, 'currentProgress:', currentProgress?.step, 'isMinimized:', isMinimized)}

            {/* Show progress indicator when loading with SSE */}
            {isLoading && currentProgress && (
              isMobile ? (
                <CompactProgressIndicator
                  step={currentProgress.step}
                  percentage={currentProgress.percentage}
                  type={currentProgress.type}
                />
              ) : (
                <ProgressIndicator
                  step={currentProgress.step}
                  message={currentProgress.message}
                  percentage={currentProgress.percentage}
                  substep={currentProgress.substep}
                  estimatedTime={currentProgress.estimatedTime}
                  type={currentProgress.type}
                />
              )
            )}

            {messages.map((message, index) => (
              <div 
                key={index} 
                className={`message ${message.type}`}
                ref={message.type === 'ai' && index === messages.length - 1 ? lastAiMessageRef : null}
              >
                {message.type === 'nudge' ? (
                  <div className="nudge-message">
                    <div className="nudge-content">
                      <div className="nudge-icon">🎯</div>
                      <div className="nudge-text">
                        <p>{message.content}</p>
                        <div className="nudge-actions">
                          <button 
                            className="unlock-coach-btn"
                            onClick={() => {
                              // Trigger onboarding flow
                              const onboardingEvent = new CustomEvent('openOnboardingFlow', { 
                                detail: { 
                                  source: 'nudge_message',
                                  context: {
                                    originalQuestion: messages[index - 2]?.content || 'general inquiry'
                                  }
                                } 
                              });
                              window.dispatchEvent(onboardingEvent);
                              
                              // Hide assessment prompt
                              setShowAssessmentPrompt(false);
                            }}
                          >
                            🎯 Personalize Gideon
                          </button>
                          <button 
                            className="dismiss-nudge-btn"
                            onClick={() => {
                              // Remove the nudge message
                              setMessages(prev => prev.filter((_, i) => i !== index));
                              setShowAssessmentPrompt(false);
                            }}
                          >
                            Not Now
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="message-content">
                      {message.role === 'assistant' ? cleanAIMessage(message.content) : message.content}
                    </div>
                    {/* React visualization rendering */}
                    {message.visualizations && message.visualizations.length > 0 && (
                      <VisualizationRenderer visualizations={message.visualizations} />
                    )}
                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="suggestions">
                        {message.suggestions.map((suggestion, suggestionIndex) => (
                          <button
                            key={suggestionIndex}
                            className="suggestion-btn"
                            onClick={() => handleSuggestionClick(suggestion)}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="message ai">
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Scroll to bottom button */}
          {showScrollButton && (
            <button 
              className="scroll-to-bottom visible"
              onClick={scrollToBottom}
              title="Scroll to bottom"
            >
              ↓
            </button>
          )}

          {/* Mobile Quick Questions */}
          {isMobile && inputValue === '' && (
            <div style={{ 
              padding: '0 24px 12px', 
              display: 'flex', 
              gap: '8px',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch'
            }}>
              {mobileQuickQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(question)}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(5, 150, 105, 0.1)',
                    border: '1px solid #059669',
                    borderRadius: '16px',
                    color: '#059669',
                    fontSize: '12px',
                    fontWeight: '600',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {question}
                </button>
              ))}
            </div>
          )}

          <div className="chat-input">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isMobile ? "Ask me anything..." : "Ask about stocks, investments, market data, or financial insights..."}
              disabled={isLoading}
              rows={isMobile ? "1" : "2"}
            />
            <button 
              onClick={handleSendMessage}
              disabled={isLoading || !inputValue.trim()}
              className="send-btn"
            >
              {isLoading ? '...' : '→'}
            </button>
          </div>
        </div>
      )}
      
      {/* No inline assessment prompt - using modal instead */}

      {/* Conversation Picker Dropdown */}
      {showConversationPicker && !isMinimized && (
        <div
          style={{
            position: 'absolute',
            top: '60px',
            right: '10px',
            background: 'rgba(10, 13, 20, 0.98)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '12px',
            padding: '12px',
            minWidth: '280px',
            maxWidth: '90vw',
            maxHeight: '400px',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            zIndex: 1000
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
            paddingBottom: '8px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h4 style={{
              margin: 0,
              color: 'white',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              Conversations
            </h4>
            <button
              onClick={() => setShowConversationPicker(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '0 4px',
                lineHeight: '1'
              }}
            >
              ×
            </button>
          </div>

          {conversationList.length === 0 ? (
            <div style={{
              padding: '20px',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.5)',
              fontSize: '13px'
            }}>
              No conversations yet
            </div>
          ) : (
            conversationList.map((conv) => (
              <div
                key={conv.sessionId}
                style={{
                  padding: '10px',
                  marginBottom: '6px',
                  background: conv.sessionId === sessionId
                    ? 'rgba(0, 225, 196, 0.1)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: conv.sessionId === sessionId
                    ? '1px solid rgba(0, 225, 196, 0.3)'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
                onMouseEnter={(e) => {
                  if (conv.sessionId !== sessionId) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (conv.sessionId !== sessionId) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  }
                }}
              >
                <div
                  onClick={() => switchToConversation(conv.sessionId)}
                  style={{ flex: 1 }}
                >
                  <div style={{
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '500',
                    marginBottom: '4px'
                  }}>
                    {conv.sessionId === sessionId && '✓ '}
                    {conv.totalMessages} messages
                  </div>
                  <div style={{
                    color: 'rgba(255, 255, 255, 0.5)',
                    fontSize: '11px'
                  }}>
                    {new Date(conv.lastActivity).toLocaleDateString()} {new Date(conv.lastActivity).toLocaleTimeString()}
                  </div>
                </div>
                {conv.sessionId !== sessionId && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm('Delete this conversation?')) {
                        deleteConversation(conv.sessionId);
                      }
                    }}
                    style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      padding: '4px 8px',
                      color: '#ef4444',
                      fontSize: '12px',
                      cursor: 'pointer',
                      marginLeft: '8px'
                    }}
                  >
                    🗑️
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}

    </div>

    {/* Suitability Assessment Modal Popup */}
    {showAssessmentPrompt && (
      <div className="suitability-modal-overlay" onClick={() => setShowAssessmentPrompt(false)}>
        <div className="suitability-modal" onClick={(e) => e.stopPropagation()}>
          <div className="suitability-modal-header">
            <button 
              className="modal-close-btn"
              onClick={() => setShowAssessmentPrompt(false)}
              title="Close"
            >
              ×
            </button>
            <div className="modal-icon">🎯</div>
            <h2 className="modal-title">Gideon Can Be Even Better!</h2>
            <p className="modal-subtitle">
              I'd love to give you personalized advice that matches your style
            </p>
          </div>
          
          <div className="suitability-modal-body">
            {/* Locked Features Section */}
            <div className="locked-features-section">
              <div className="locked-features-title">What I Can Personalize</div>
              <div className="locked-feature-item">Options & Derivatives Trading</div>
              <div className="locked-feature-item">Margin Trading & Leverage</div>
              <div className="locked-feature-item">Crypto ETFs & Alternative Assets</div>
              <div className="locked-feature-item">Advanced Trading Strategies</div>
            </div>
            
            {/* Benefits Section */}
            <div className="benefits-section">
              <div className="benefits-title">What You'll Get</div>
              {showAssessmentPrompt.benefits && showAssessmentPrompt.benefits.map((benefit, index) => (
                <div key={index} className="benefit-item">
                  {benefit}
                </div>
              ))}
            </div>
            
            {/* Gideon's Message */}
            <div className="gideon-message">
              <div className="gideon-says">Gideon Says</div>
              <div className="gideon-text">
                {showAssessmentPrompt.message || "Hey! I'd love to help you unlock these advanced features! Let's have a quick chat so I can understand your investment style and give you personalized advice. It'll be fun and educational! 🚀"}
              </div>
            </div>
            
            {/* Time Indicator */}
            <div className="time-indicator">
              <span className="time-text">
                ⏱️ Takes only <strong>2-3 minutes</strong> to complete
              </span>
            </div>
          </div>
          
          <div className="suitability-modal-actions">
            <button 
              className="chat-with-gideon-btn"
              onClick={() => {
                handleStartAssessment(showAssessmentPrompt.originalQuestion);
                setShowAssessmentPrompt(false);
              }}
            >
              Let's Chat with Gideon! 🚀
            </button>
            <button 
              className="maybe-later-btn"
              onClick={() => setShowAssessmentPrompt(false)}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default FloatingAIAssistant;